import { NextCustomImage } from 'components/Image';
import { type MutableRefObject, useEffect, useRef, useState } from 'react';
import RawHtml from 'utils/miscUtils/rawHtml';
import Link from 'components/Globals/Base/Link';
import Button from 'components/Globals/Base/Button';
import { withErrorBoundary } from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import EDUCATION_RANKINGS_TEST_IDS from 'constants/testsIds/EducationRankings';
import { IMAGE_TEST_IDS } from 'constants/testsIds/homepage';
import * as S from './ProductCardStyles';
import ContentTable, { type ContentTableProps } from './ContentTable';
import ProductCardSections, { type SectionValue } from './ProductCardSections';

export interface ProductCardProps {
  className?: string;
  productCardData: {
    isAdvertisement: { text: string; value: boolean };
    image?: { src: string; alt: string; caption?: string };
    captionLink?: string;
    title: string;
    titleLink?: string;
    description?: {
      text?: string;
      tables?: { values: { title: string; value: string; toggletipContent?: string }[] }[];
    };
    bottomSection?: {
      text?: string;
      tables?: { values: { title: string; value: string; toggletipContent?: string }[] }[];
    };
    creditCardsSection?: { text: string; title: string };
    subtitle?: string;
    ctaButton?: { text: string; href: string; caption?: string };
    sections: SectionValue[];
    type?: string;
    id?: string;
  };
  dataCy?: string;
}

const ProductCard = ({ productCardData, className, dataCy }: ProductCardProps) => {
  const [sectionOpen, setSectionOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReadMoreButton, setShowReadMoreButton] = useState(true);
  const ref: MutableRefObject<HTMLElement | null> = useRef(null);
  const handleChildData = (dataFromChild: boolean) => {
    setSectionOpen(dataFromChild);
  };

  // This is rendered on server side, so useLayoutEffect can't be used
  useEffect(() => {
    if (ref.current && ref.current.clientHeight === ref.current.scrollHeight) {
      setShowReadMoreButton(false);
    }
  }, [ref]);

  return (
    <S.CardWrapper
      className={className}
      $isAdvertisement={!!(productCardData.isAdvertisement?.text && productCardData.isAdvertisement?.value)}
      id={productCardData.id}
      data-cy={dataCy}
    >
      {productCardData.isAdvertisement?.text && productCardData.isAdvertisement?.value && (
        <S.AdvertisementDisclaimer>{productCardData.isAdvertisement.text}</S.AdvertisementDisclaimer>
      )}
      <S.ContentWrapper className='content-wrapper'>
        {(productCardData.image || productCardData.ctaButton) && (
          <S.RightColumn className='right-column'>
            {productCardData.image?.src && (
              <div
                className='image-cluster'
                data-cy={EDUCATION_RANKINGS_TEST_IDS.PRODUCT_CARD_IMAGE}
              >
                <NextCustomImage
                  src={productCardData.image.src}
                  alt={productCardData.image.alt ? productCardData.image.alt : productCardData.title}
                  width={168}
                  height={80}
                  sizes='10vw'
                  className='image'
                  priority
                  data-cy={IMAGE_TEST_IDS.IMAGE}
                />
                {productCardData.image.caption && <S.Caption>{productCardData.image.caption}</S.Caption>}
                {productCardData.captionLink && (
                  <S.Caption
                    href={productCardData.captionLink}
                    link
                  >
                    See more rates
                  </S.Caption>
                )}
              </div>
            )}
            {productCardData.ctaButton && (
              <span className='button-desktop-wrapper'>
                <S.Button
                  href={productCardData.ctaButton.href}
                  target='_blank'
                  className={`button-desktop ${!productCardData.image ? 'no-image' : ''}`}
                  type='primaryBrandButton'
                  size={{ default: 'large' }}
                >
                  {productCardData.ctaButton.text}
                </S.Button>
                {productCardData?.ctaButton?.caption && (
                  <S.Caption as='p'>{productCardData.ctaButton.caption}</S.Caption>
                )}
              </span>
            )}
          </S.RightColumn>
        )}
        <S.Content
          className='content'
          data-cy={EDUCATION_RANKINGS_TEST_IDS.PRODUCT_CARD_CONTENT}
        >
          <S.Title
            as='h4'
            className={`card-title ${productCardData.subtitle ? 'has-subtitle' : ''}`}
            $fontFamily='graphikCond'
            $size={{ default: 'level3', mdMin: 'level2' }}
          >
            <Link
              href={productCardData.titleLink ? productCardData.titleLink : productCardData.ctaButton?.href}
              ariaLabel={`Go to ${productCardData.title} page`}
              target={productCardData.titleLink ? '_self' : '_blank'}
              rel={productCardData.titleLink ? '' : 'noopener noreferrer'}
              type='primary'
            >
              {productCardData.title}
            </Link>
          </S.Title>
          {productCardData.subtitle && (
            <S.Subtitle
              className={`${productCardData.isAdvertisement?.value ? 'is-advertisement' : ''} ${
                productCardData.description ? 'has-description' : ''
              }`}
            >
              {' '}
              {productCardData.subtitle}{' '}
            </S.Subtitle>
          )}
          {productCardData.creditCardsSection && (
            <S.CreditCardsSection>
              <div className='credit-section-title'>{productCardData.creditCardsSection.title}</div>
              <div className='credit-section-text'>{productCardData.creditCardsSection.text}</div>
            </S.CreditCardsSection>
          )}
          {productCardData.description && (
            <>
              <S.Description
                ref={ref}
                $isExpanded={isExpanded}
              >
                <summary>{RawHtml({ html: productCardData.description.text })}</summary>
                {productCardData.description.tables &&
                  productCardData.description.tables.map((table: ContentTableProps, index: number) => (
                    <ContentTable
                      key={`${table.title}-${index}`}
                      title={table.title}
                      values={table.values}
                    />
                  ))}
              </S.Description>
              {showReadMoreButton && (
                <Button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className='read-more-button'
                  type='button'
                  variant='tertiaryBrand'
                  arrowType={isExpanded ? 'up-arrow' : 'down-arrow'}
                >
                  {isExpanded ? 'Read Less' : 'Read More'}
                </Button>
              )}
            </>
          )}
          {productCardData.ctaButton && (
            <span className='button-mobile-wrapper'>
              <S.Button
                href={productCardData.ctaButton?.href}
                target='_blank'
                className='button-mobile'
                type='primaryBrandButton'
                size={{ default: 'large' }}
              >
                {productCardData.ctaButton?.text}
              </S.Button>
              {productCardData?.ctaButton?.caption && <S.Caption as='p'>{productCardData.ctaButton.caption}</S.Caption>}
            </span>
          )}
        </S.Content>
        {productCardData.bottomSection && (
          <S.BottomSection
            className='bottom-section'
            data-cy={EDUCATION_RANKINGS_TEST_IDS.PRODUCT_CARD_BOTTOM}
          >
            {productCardData.bottomSection.text && <p>{RawHtml({ html: productCardData.bottomSection.text })}</p>}
            {productCardData.bottomSection.tables &&
              productCardData.bottomSection.tables.map((table: ContentTableProps, index: number) => (
                <ContentTable
                  key={`${table.title}-${index}`}
                  title={table.title}
                  values={table.values}
                />
              ))}
          </S.BottomSection>
        )}
        <ProductCardSections
          values={productCardData.sections}
          handleSectionOpen={handleChildData}
        />
        {sectionOpen && productCardData.ctaButton && (
          <span className='button-mobile-wrapper'>
            <S.Button
              href={productCardData.ctaButton?.href}
              target='_blank'
              className='button-mobile'
              type='primaryBrandButton'
              size={{ default: 'large' }}
            >
              {productCardData.ctaButton?.text}
            </S.Button>
            {productCardData?.ctaButton?.caption && <S.Caption as='p'>{productCardData.ctaButton.caption}</S.Caption>}
          </span>
        )}
      </S.ContentWrapper>
    </S.CardWrapper>
  );
};

export default withErrorBoundary(ProductCard, { fragment: UIFragments.EDU_PRODUCT_CARD });
